import { Injectable } from '@angular/core';
import { UserSettings } from '@model/user/user-settings';
import { BehaviorSubject, fromEvent, merge, Observable, Subject } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { LocalSettingsKey } from '@shared/types/local-settings';

@Injectable({
    providedIn: 'root'
})
export class UserSettingsStorage {
    userSettings = new BehaviorSubject<UserSettings>({} as any);
    userSettingsRefresher = new BehaviorSubject(false);
    languageRefresher = new BehaviorSubject<string>('');
    themeChanger = new BehaviorSubject(false);
    reminderSwitchRefresher = new Subject<string>();
    bannerRefresher = new Subject<string>();

    constructor() { }

    getUserSettings(): Observable<UserSettings> {
        return this.userSettings.asObservable();
    }

    getUserSettingsValues(): UserSettings {
        return this.userSettings.getValue();
    }

    setUserSettings(userSettings): void {
        this.userSettings.next(userSettings);
    }

    refreshUserSettings(isUpdate: boolean) {
        this.userSettingsRefresher.next(isUpdate);
    }

    /* Check if user is updated or not */
    getUserSettingsRefresher(): Observable<boolean> {
        return this.userSettingsRefresher.asObservable();
    }

    refreshUserSettingsInPopup(): void {
        localStorage.setItem(LocalSettingsKey.SETTINGS_UPDATED, 'true');
        localStorage.removeItem(LocalSettingsKey.SETTINGS_UPDATED);
    }

    listenUserSettingsRefresherInPopup(): Observable<string> {
        return fromEvent<StorageEvent>(window, 'storage').pipe(
            filter(event => event.key === LocalSettingsKey.SETTINGS_UPDATED),
            map(event => {
                return event.newValue;
            })
        );
    }

    setTranslateLanguage(language: string): void {
        this.languageRefresher.next(language);
    }

    listenLanguageChange(): Observable<string> {
        return this.languageRefresher.asObservable();
    }

    listenLanguageRefresher(): Observable<string> {
        return fromEvent<StorageEvent>(window, 'storage').pipe(
            filter(event => event.key === LocalSettingsKey.TRANSLATE),
            map(event => {
                return event.newValue;
            })
        );
    }

    /* for refreshing in both of main and popup windows */
    refreshProfilePhoto(): void {
        localStorage.setItem(LocalSettingsKey.PROFILE_PHOTO_UPDATED, 'true');
        localStorage.removeItem(LocalSettingsKey.PROFILE_PHOTO_UPDATED);
    }

    listenProfilePhotoRefresher(): Observable<string> {
        return fromEvent<StorageEvent>(window, 'storage').pipe(
            filter(event => event.key === LocalSettingsKey.PROFILE_PHOTO_UPDATED),
            map(event => {
                return event.newValue;
            })
        );
    }

    changeTheme(): void {
        this.themeChanger.next(true);
    }

    /* for enabling reminders when multiple tabs are open */
    refreshRemindersSwitch(isEnabled: boolean): void {
        this.reminderSwitchRefresher.next((isEnabled || false).toString());
        // local storage refresher works on other tabs, not the current so we have to trigger reminderSwitchRefresher since reminder component exists in layout component
        localStorage.setItem(LocalSettingsKey.REMINDER_SWITCH_UPDATED, (isEnabled || false).toString());
        localStorage.removeItem(LocalSettingsKey.REMINDER_SWITCH_UPDATED);
    }

    listenRemindersSwitchRefresher(): Observable<string> {
        return merge(
            this.getRemindersSwitchStorageRefresher(),
            this.reminderSwitchRefresher.asObservable(),
            this.bannerRefresher.asObservable()
        );
    }

    getRemindersSwitchStorageRefresher(): Observable<string> {
        return fromEvent<StorageEvent>(window, 'storage').pipe(
            filter(event => event.key === LocalSettingsKey.REMINDER_SWITCH_UPDATED),
            map(event => event.newValue)
        );
    }

    isBannerSeen(bannerKey: string): boolean {
        return (localStorage.getItem(`${LocalSettingsKey.IS_BANNER_SEEN}${bannerKey}`) || '0') === '1';
    }

    setIsBannerSeen(bannerKey: string): void {
        localStorage.setItem(`${LocalSettingsKey.IS_BANNER_SEEN}${bannerKey}`, '1');
        this.bannerRefresher.next('true');
    }
}
